"use client";

import { readReportDebugFlag } from "@/lib/navigation/debug-flags";

const PERSISTED_HISTORY_EVENT_KEY = "clientReportDebugLastHistoryEvent";
const PERSISTED_LIFECYCLE_EVENT_KEY = "clientReportDebugLastLifecycleEvent";
const PERSISTED_STARTUP_TIMELINE_KEY = "clientReportDebugStartupTimeline";
const PERSISTED_PROGRAM_HOME_BOUNCE_KEY = "clientReportDebugProgramHomeBounce";
const PERSISTED_GALLERY_HOME_BOUNCE_KEY = "clientReportDebugGalleryHomeBounce";
const PERSISTED_PROGRAM_HOME_RAW_HISTORY_KEY = "clientReportDebugProgramHomeRawHistory";
const PERSISTED_NUTRITION_HOME_BOUNCE_KEY = "clientReportDebugNutritionHomeBounce";
const PERSISTED_RENDERED_ROUTE_SPLIT_BRAIN_KEY = "clientReportDebugRenderedRouteSplitBrain";
const PERSISTED_NUTRITION_ROUTE_GUARD_DECISIONS_KEY = "clientReportDebugNutritionRouteGuardDecisions";
const PERSISTED_NUTRITION_ROUTE_GUARD_DECISIONS_MAX = 20;
const PERSISTED_HISTORY_EDIT_NAVIGATION_DEBUG_KEY = "clientReportDebugHistoryEditNavigationDebug";
const PERSISTED_HISTORY_EDIT_SCROLL_DEBUG_KEY = "clientReportDebugHistoryEditScrollDebug";
const PERSISTED_HOME_WEIGHT_GUARD_DECISIONS_KEY = "clientReportDebugHomeWeightGuardDecisions";
const PERSISTED_HOME_WEIGHT_GUARD_DECISIONS_MAX = 20;
const PERSISTED_WEIGHT_MEASUREMENTS_GUARD_DECISIONS_KEY = "clientReportDebugWeightMeasurementsGuardDecisions";
const PERSISTED_WEIGHT_MEASUREMENTS_GUARD_DECISIONS_MAX = 20;
const PERSISTED_JS_CONTEXT_GENERATION_KEY = "clientReportDebugJsContextGeneration";
const PERSISTED_STARTUP_TIMELINE_MAX = 40;
const PERSISTED_HOME_PROGRAM_CORRECTION_KEY = "clientReportDebugHomeProgramCorrection";
let persistedHomeProgramCorrectionSnapshot: PersistedHomeProgramCorrectionDebugSnapshot | null = null;
let persistedProgramHomeBounceSnapshot: PersistedProgramHomeBounceEvent | null = null;
let persistedGalleryHomeBounceSnapshot: PersistedGalleryHomeBounceEvent | null = null;
let persistedProgramHomeRawHistorySnapshot: PersistedProgramHomeRawHistoryDebugSnapshot | null = null;
let persistedNutritionHomeBounceSnapshot: PersistedNutritionHomeBounceEvent | null = null;
let persistedRenderedRouteSplitBrainSnapshot: PersistedRenderedRouteSplitBrainSnapshot | null = null;
let persistedHistoryEditNavigationDebugSnapshot: PersistedHistoryEditNavigationDebugSnapshot | null = null;

export type TransactionSnapshotPayload = {
  id: string | null;
  fromPath: string | null;
  toPath: string | null;
  source: string | null;
  reason: string | null;
  accepted: boolean | null;
  routeFamilyFrom: string | null;
  routeFamilyTo: string | null;
  ageMs: number | null;
  consumedAtIso?: string | null;
  consumedReason?: string | null;
  expiredAtIso?: string | null;
};

export type IntentSnapshotPayload = {
  kind: string | null;
  target: string | null;
  isActionable: boolean | null;
  href: string | null;
  text: string | null;
  routeAtIntent: string | null;
};

type PersistedHistoryEvent = {
  event: "pushState" | "replaceState" | "popstate";
  ts: number;
  atIso: string;
  url: string;
  path: string | null;
  href: string | null;
  pathname: string | null;
  stackSummary: string | null;
  stackPreview: string | null;
  fullStack: string | null;
  jsContextGeneration: string | null;
  clientNavTransactionId: string | null;
  acceptedTransactionId: string | null;
  callerFnName: string | null;
  callerFileName: string | null;
  callerLineNumber: number | null;
  appLevelCallerFnName: string | null;
  appLevelCallerFileName: string | null;
  appLevelCallerLineNumber: number | null;
  acceptedTransactionFromPath: string | null;
  acceptedTransactionToPath: string | null;
  historyStateKeys: string | null;
  historyStateIdx: number | null;
  clientNavTransaction: TransactionSnapshotPayload | null;
  acceptedTransaction: TransactionSnapshotPayload | null;
  previousPath: string | null;
  previousHref: string | null;
  lastIntent: IntentSnapshotPayload | null;
  lastActionableIntent: IntentSnapshotPayload | null;
  historyTreeSummary: string | null;
  latestNavReplace: boolean | null;
  bottomNavStalePushState: Record<string, unknown> | null;
};

export function buildTransactionSnapshotPayload(
  transaction: {
    transactionId?: string | null;
    fromPath?: string | null;
    toPath?: string | null;
    source?: string | null;
    reason?: string | null;
    accepted?: boolean | null;
    routeFamilyFrom?: string | null;
    routeFamilyTo?: string | null;
    initiatedAtIso?: string | null;
    consumedAtIso?: string | null;
    consumedReason?: string | null;
    expiredAtIso?: string | null;
  } | null,
  nowMs: number
): TransactionSnapshotPayload | null {
  if (!transaction) return null;
  const initiatedAtMs = transaction.initiatedAtIso ? Date.parse(transaction.initiatedAtIso) : NaN;
  return {
    id: transaction.transactionId ?? null,
    fromPath: transaction.fromPath ?? null,
    toPath: transaction.toPath ?? null,
    source: transaction.source ?? null,
    reason: transaction.reason ?? null,
    accepted: transaction.accepted ?? null,
    routeFamilyFrom: transaction.routeFamilyFrom ?? null,
    routeFamilyTo: transaction.routeFamilyTo ?? null,
    ageMs: Number.isFinite(initiatedAtMs) ? Math.max(0, nowMs - initiatedAtMs) : null,
    consumedAtIso: transaction.consumedAtIso ?? null,
    consumedReason: transaction.consumedReason ?? null,
    expiredAtIso: transaction.expiredAtIso ?? null,
  };
}

export function buildIntentSnapshotPayload(
  intent: {
    kind?: string;
    target?: string;
    isActionable?: boolean;
    actionableHref?: string;
    actionableText?: string;
    routeAtIntent?: string;
  } | null
): IntentSnapshotPayload | null {
  if (!intent) return null;
  return {
    kind: intent.kind ?? null,
    target: intent.target ?? null,
    isActionable: intent.isActionable ?? null,
    href: intent.actionableHref ?? null,
    text: intent.actionableText ?? null,
    routeAtIntent: intent.routeAtIntent ?? null,
  };
}

type PersistedLifecycleEvent = {
  event: "visibilitychange" | "pagehide" | "pageshow" | "focus" | "blur";
  value: string;
  ts: number;
  atIso: string;
  href: string | null;
  pathname: string | null;
  visibilityState: string | null;
};

type PersistedProgramHomeBounceEvent = {
  fromPath: string;
  toPath: string;
  ts: number;
  intent: string | null;
  intentAgeMs: number | null;
  popstateAgeMs: number | null;
  navEvent: string | null;
  navSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  intentKind: string | null;
  intentTarget: string | null;
  intentActionableHref: string | null;
  intentActionableText: string | null;
  historyEvent: string | null;
  historyUrl: string | null;
  correctionReason: string | null;
};

export type PersistedHomeProgramCorrectionDebugSnapshot = {
  fromPath: string;
  bouncedPath: string;
  ts: number;
  navEvent: string | null;
  navSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  popstateAgeMs: number | null;
  intentKind: string | null;
  intentTarget: string | null;
  intentActionableHref: string | null;
  historyStackSummary: string | null;
  latestTransactionId: string | null;
  latestTransactionSource: string | null;
  latestTransactionFromPath: string | null;
  latestTransactionToPath: string | null;
  latestTransactionAccepted: boolean | null;
  latestTransactionAgeMs: number | null;
};

type PersistedGalleryHomeBounceEvent = {
  fromPath: string;
  toPath: string;
  ts: number;
  intent: string | null;
  intentAgeMs: number | null;
  popstateAgeMs: number | null;
  navEvent: string | null;
  navSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  intentKind: string | null;
  intentTarget: string | null;
  intentActionableHref: string | null;
  intentActionableText: string | null;
  historyEvent: string | null;
  historyUrl: string | null;
  correctionReason: string | null;
  latestTransactionSource: string | null;
  latestTransactionReason: string | null;
  latestTransactionFromPath: string | null;
  latestTransactionToPath: string | null;
  latestTransactionAccepted: boolean | null;
};

export type PersistedProgramHomeRawHistoryDebugSnapshot = {
  event: "program-home-raw-history-route-change-without-accepted-transaction";
  ts: number;
  fromPath: string;
  toPath: string | null;
  currentPathname: string | null;
  url: string;
  stackSummary: string | null;
  intent: string | null;
  intentAgeMs: number | null;
  popstateAgeMs: number | null;
  navEvent: string | null;
  navSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  intentKind: string | null;
  intentTarget: string | null;
  intentActionableHref: string | null;
  intentActionableText: string | null;
  latestActionableIntentHref: string | null;
  latestActionableIntentRouteAtIntent: string | null;
  latestActionableIntentAgeMs: number | null;
  historyEvent: string | null;
  correctionReason: string | null;
  latestClientNavTransactionId: string | null;
  latestClientNavTransactionSource: string | null;
  latestClientNavTransactionFromPath: string | null;
  latestClientNavTransactionToPath: string | null;
  latestClientNavTransactionAccepted: boolean | null;
  latestClientNavTransactionAgeMs: number | null;
  latestAcceptedTransactionId: string | null;
  latestAcceptedTransactionSource: string | null;
  latestAcceptedTransactionFromPath: string | null;
  latestAcceptedTransactionToPath: string | null;
  latestAcceptedTransactionAccepted: boolean | null;
  latestAcceptedTransactionAgeMs: number | null;
  currentJsContextGeneration: string | null;
  latestAcceptedTransactionJsContextGeneration: string | null;
  jsContextGenerationMismatch: boolean | null;
  transactionStoreEmpty: boolean | null;
  routePairKind: "program_to_home";
  derivedFromRawHistoryEvent: true;
};

type PersistedNutritionHomeBounceEvent = {
  fromPath: string;
  toPath: string;
  ts: number;
  intent: string | null;
  intentAgeMs: number | null;
  popstateAgeMs: number | null;
  navEvent: string | null;
  navSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  intentKind: string | null;
  intentTarget: string | null;
  intentActionableHref: string | null;
  intentActionableText: string | null;
  historyEvent: string | null;
  historyUrl: string | null;
  correctionReason: string | null;
};

export type PersistedRenderedRouteSplitBrainSnapshot = {
  ts: number;
  atIso: string;
  reason: "rendered-route-browser-href-mismatch";
  renderedPath: string;
  browserPath: string;
  browserHref: string;
  previousPath: string | null;
  previousHref: string | null;
  routeTransitionActive: boolean;
  latestClientNavTransaction: TransactionSnapshotPayload | null;
  latestAcceptedTransaction: TransactionSnapshotPayload | null;
  latestHistoryEvent: {
    event: string;
    url: string;
    ageMs: number | null;
  } | null;
  latestIntent: IntentSnapshotPayload | null;
  sampleDelayMs: number;
  jsContextGeneration: string | null;
  clientShellMountId?: number | null;
  clientShellMountCount?: number | null;
  clientShellActiveMountCount?: number | null;
};

type PersistedNutritionRouteGuardDecisionEvent = {
  ts: number;
  atIso: string;
  guard: "nutrition-home" | "home-nutrition";
  decision: "allow" | "replace" | "skip-early" | "skip-recent-correction";
  reason: string;
  previousPath: string | null;
  currentPath: string | null;
  previousHref: string | null;
  browserLocationHref: string | null;
  intentKind: string | null;
  intentIsActionable: boolean | null;
  intentAgeMs: number | null;
  intentRouteAtIntent: string | null;
  intentActionableHref: string | null;
  intentActionableText: string | null;
  intentInBottomTabs: boolean | null;
  lastActionableIntentHref: string | null;
  lastActionableIntentAgeMs: number | null;
  lastActionableIntentRouteAtIntent: string | null;
  navEvent: string | null;
  navSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  popstateAgeMs: number | null;
  historyEvent: string | null;
  historyAgeMs: number | null;
  hasTransaction: boolean | null;
  latestTransactionSource: string | null;
  latestTransactionFromPath: string | null;
  latestTransactionToPath: string | null;
  latestTransactionReason: string | null;
  latestTransactionAccepted: boolean | null;
  latestTransactionAgeMs: number | null;
  restoreHref: string | null;
  shadowDecisionKind: string | null;
  shadowDecisionReason: string | null;
  shadowDecisionHandled: boolean | null;
};

type PersistedStartupTimelineEvent = {
  source: string;
  event: string;
  ts: number;
  atIso: string;
  path: string | null;
  href: string | null;
  visibilityState: string | null;
  details?: Record<string, unknown>;
};

type PersistedHistoryEditScrollDebugSnapshot = {
  snapshotAtIso: string;
  routePath: string | null;
  source: string;
  reason: string;
  activeElement: Record<string, unknown> | null;
  textEntryActive: boolean | null;
  keyboardOpen: boolean | null;
  windowScrollY: number | null;
  windowInnerHeight: number | null;
  visualViewport: {
    height: number | null;
    width: number | null;
    offsetTop: number | null;
    pageTop: number | null;
  } | null;
  shellScrollContainer: {
    scrollTop: number | null;
    clientHeight: number | null;
    scrollHeight: number | null;
    rect: Record<string, unknown> | null;
  } | null;
  historyEditRoot: {
    rect: Record<string, unknown> | null;
  } | null;
  focusRevealScrollArea: {
    scrollTop: number | null;
    clientHeight: number | null;
    scrollHeight: number | null;
    rect: Record<string, unknown> | null;
  } | null;
};

export type PersistedHistoryEditNavigationDebugSnapshot = {
  snapshotAtIso: string;
  routePath: string | null;
  source: string;
  event: "history-edit-open" | "history-edit-back";
  currentHref: string | null;
  targetHref: string | null;
  resolvedBackHref: string | null;
  sessionId: string | null;
  activeSessionId: string | null;
  lastActiveSessionId: string | null;
  lastActiveSessionUrl: string | null;
  historyLength: number | null;
  outcome: string | null;
};

type PersistedHomeWeightGuardDecisionEvent = {
  ts: number;
  atIso: string;
  decision: "allow" | "bounce" | "skip-early" | "skip-popstate" | "skip-intent" | "skip-transaction" | "skip-recent-correction";
  skipReason?: string;
  previousPath: string | null;
  currentPath: string | null;
  previousPathRef: string | null;
  pathname: string | null;
  browserLocationPath: string | null;
  previousPathMatchesBrowser: boolean | null;
  intentKind: string | null;
  intentIsActionable: boolean | null;
  intentAgeMs: number | null;
  popstateAgeMs: number | null;
  navEvent: string | null;
  navSource: string | null;
  navAgeMs: number | null;
  historyEvent: string | null;
  historyAgeMs: number | null;
  hasTransaction: boolean | null;
  shadowDecisionKind: string | null;
  shadowDecisionReason: string | null;
};

type PersistedWeightMeasurementsGuardDecisionEvent = {
  ts: number;
  atIso: string;
  previousPath: string | null;
  currentPath: string | null;
  decision: "allow-popstate" | "allow-deterministic" | "allow-intent" | "allow-actionable-intent" | "allow-transaction" | "allow-nav-event" | "allow-missing-intent" | "skip-recent-correction" | "bounce";
  skipReason: string | null;
  deterministicRouteActive: boolean | null;
  clientNavResolverV1Active: boolean | null;
  intentKind: string | null;
  intentIsActionable: boolean | null;
  intentAgeMs: number | null;
  intentRouteAtIntent: string | null;
  intentPath: string | null;
  popstateAgeMs: number | null;
  navEventSource: string | null;
  navHrefPath: string | null;
  navAgeMs: number | null;
  transactionSource: string | null;
  transactionReason: string | null;
  transactionFromPath: string | null;
  transactionToPath: string | null;
  transactionAccepted: boolean | null;
  transactionAgeMs: number | null;
  intentCheckFailed: string | null;
  lastActionableCheckFailed: string | null;
  transactionCheckFailed: string | null;
  navEventCheckFailed: string | null;
  lastActionableHref: string | null;
  lastActionableRouteAtIntent: string | null;
  lastActionableAgeMs: number | null;
  shadowDecisionKind: string | null;
  shadowDecisionReason: string | null;
  shadowDecisionHandled: boolean | null;
};

function snapshotRect(element: Element | null) {
  if (!(element instanceof Element)) return null;
  const rect = element.getBoundingClientRect();
  return {
    top: Math.round(rect.top * 100) / 100,
    right: Math.round(rect.right * 100) / 100,
    bottom: Math.round(rect.bottom * 100) / 100,
    left: Math.round(rect.left * 100) / 100,
    width: Math.round(rect.width * 100) / 100,
    height: Math.round(rect.height * 100) / 100,
  };
}

function snapshotActiveElement(element: Element | null) {
  if (!(element instanceof HTMLElement)) return null;
  return {
    tagName: element.tagName.toLowerCase(),
    id: element.id || null,
    className: element.className || null,
    type: element.getAttribute("type"),
    role: element.getAttribute("role"),
    ariaLabel: element.getAttribute("aria-label"),
    placeholder: element.getAttribute("placeholder"),
    inputMode: element.getAttribute("inputmode"),
    name: element.getAttribute("name"),
    dataset: Object.fromEntries(Object.entries(element.dataset).slice(0, 8)),
  };
}

function isHistoryEditScrollDebugRoute(pathname: string | null): boolean {
  return typeof pathname === "string" && pathname.startsWith("/client/history/") && pathname.endsWith("/edit");
}

function buildHistoryEditScrollDebugSnapshotFromDom(
  reason: string
): PersistedHistoryEditScrollDebugSnapshot | null {
  if (typeof window === "undefined") return null;
  if (!isHistoryEditScrollDebugRoute(window.location.pathname)) return null;

  const root = document.querySelector(".history-edit-workout-session");
  const scrollArea = document.querySelector(".client-focus-reveal-scroll-area");
  const shellScrollContainer = document.querySelector(".client-shell-scroll-container");
  if (!(root instanceof Element) || !(scrollArea instanceof HTMLElement)) return null;

  const visualViewport = window.visualViewport;
  return {
    snapshotAtIso: new Date().toISOString(),
    routePath: window.location.pathname + window.location.search,
    source: "history-edit-page",
    reason,
    activeElement: snapshotActiveElement(document.activeElement),
    textEntryActive: document.documentElement.dataset.clientTextEntryActive === "1",
    keyboardOpen: document.documentElement.dataset.clientKeyboardOpen === "1",
    windowScrollY: window.scrollY,
    windowInnerHeight: window.innerHeight,
    visualViewport: visualViewport
      ? {
          height: Math.round(visualViewport.height * 100) / 100,
          width: Math.round(visualViewport.width * 100) / 100,
          offsetTop: Math.round(visualViewport.offsetTop * 100) / 100,
          pageTop: Math.round(visualViewport.pageTop * 100) / 100,
        }
      : null,
    shellScrollContainer: shellScrollContainer instanceof HTMLElement
      ? {
          scrollTop: Math.round(shellScrollContainer.scrollTop * 100) / 100,
          clientHeight: Math.round(shellScrollContainer.clientHeight * 100) / 100,
          scrollHeight: Math.round(shellScrollContainer.scrollHeight * 100) / 100,
          rect: snapshotRect(shellScrollContainer),
        }
      : null,
    historyEditRoot: {
      rect: snapshotRect(root),
    },
    focusRevealScrollArea: {
      scrollTop: Math.round(scrollArea.scrollTop * 100) / 100,
      clientHeight: Math.round(scrollArea.clientHeight * 100) / 100,
      scrollHeight: Math.round(scrollArea.scrollHeight * 100) / 100,
      rect: snapshotRect(scrollArea),
    },
  };
}

function getLocationHref() {
  if (typeof window === "undefined") return null;
  return window.location.href;
}

function getLocationPath() {
  if (typeof window === "undefined") return null;
  return `${window.location.pathname}${window.location.search}`;
}

function safePathFromHref(href: string | null | undefined): string | null {
  if (!href || typeof window === "undefined") return null;

  try {
    const parsed = new URL(href, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

function sameOriginReferrerPath(referrer: string | null | undefined): string | null {
  if (!referrer || typeof window === "undefined") return null;

  try {
    const parsed = new URL(referrer, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}

function writePersistedSnapshot(key: string, snapshot: unknown) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Ignore storage failures in debug-only instrumentation.
  }
}

function readPersistedSnapshot<T>(key: string): T | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function readPersistedArray<T>(key: string): T[] {
  const parsed = readPersistedSnapshot<T[]>(key);
  return Array.isArray(parsed) ? parsed : [];
}

function generateJsContextGeneration(): string {
  const id = `jsg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  try {
    window.sessionStorage.setItem(PERSISTED_JS_CONTEXT_GENERATION_KEY, id);
  } catch {
    // Ignore storage failures in debug-only instrumentation.
  }
  return id;
}

export function getJsContextGeneration(): string {
  if (typeof window === "undefined") return "";
  try {
    const stored = window.sessionStorage.getItem(PERSISTED_JS_CONTEXT_GENERATION_KEY);
    if (stored) return stored;
  } catch {
    // Ignore storage failures.
  }
  return generateJsContextGeneration();
}

function sanitizeTimelineDetails(details: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!details) return undefined;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(details).slice(0, 16)) {
    if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      result[key] = value;
      continue;
    }
    result[key] = String(value);
  }
  return result;
}

function getNavigationEntry(): PerformanceNavigationTiming | null {
  if (typeof window === "undefined" || typeof window.performance?.getEntriesByType !== "function") {
    return null;
  }

  const [entry] = window.performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  if (!entry || typeof entry.type !== "string") return null;
  return entry;
}

function getNavigationActivationStartMs(entry: PerformanceNavigationTiming | null): number | null {
  if (!entry) return null;

  const activationStart = (entry as PerformanceNavigationTiming & { activationStart?: unknown }).activationStart;
  return typeof activationStart === "number" && Number.isFinite(activationStart)
    ? Math.round(activationStart)
    : null;
}

export function persistReportDebugHistorySnapshot(params: {
  event: PersistedHistoryEvent["event"];
  url: string;
  ts?: number;
  stackSummary?: string | null;
  stackPreview?: string | null;
  fullStack?: string | null;
  clientNavTransactionId?: string | null;
  acceptedTransactionId?: string | null;
  callerFnName?: string | null;
  callerFileName?: string | null;
  callerLineNumber?: number | null;
  appLevelCallerFnName?: string | null;
  appLevelCallerFileName?: string | null;
  appLevelCallerLineNumber?: number | null;
  acceptedTransactionFromPath?: string | null;
  acceptedTransactionToPath?: string | null;
  historyStateKeys?: string | null;
  historyStateIdx?: number | null;
  clientNavTransaction?: TransactionSnapshotPayload | null;
  acceptedTransaction?: TransactionSnapshotPayload | null;
  previousPath?: string | null;
  previousHref?: string | null;
  lastIntent?: IntentSnapshotPayload | null;
  lastActionableIntent?: IntentSnapshotPayload | null;
  historyTreeSummary?: string | null;
  latestNavReplace?: boolean | null;
  bottomNavStalePushState?: Record<string, unknown> | null;
}) {
  if (typeof window === "undefined") return;

  const ts = typeof params.ts === "number" ? params.ts : Date.now();
  writePersistedSnapshot(PERSISTED_HISTORY_EVENT_KEY, {
    event: params.event,
    ts,
    atIso: new Date(ts).toISOString(),
    url: params.url,
    path: safePathFromHref(params.url),
    href: getLocationHref(),
    pathname: getLocationPath(),
    stackSummary: params.stackSummary ?? null,
    stackPreview: params.stackPreview ?? null,
    fullStack: params.fullStack ?? null,
    jsContextGeneration: getJsContextGeneration(),
    clientNavTransactionId: params.clientNavTransactionId ?? null,
    acceptedTransactionId: params.acceptedTransactionId ?? null,
    callerFnName: params.callerFnName ?? null,
    callerFileName: params.callerFileName ?? null,
    callerLineNumber: params.callerLineNumber ?? null,
    appLevelCallerFnName: params.appLevelCallerFnName ?? null,
    appLevelCallerFileName: params.appLevelCallerFileName ?? null,
    appLevelCallerLineNumber: params.appLevelCallerLineNumber ?? null,
    acceptedTransactionFromPath: params.acceptedTransactionFromPath ?? null,
    acceptedTransactionToPath: params.acceptedTransactionToPath ?? null,
    historyStateKeys: params.historyStateKeys ?? null,
    historyStateIdx: params.historyStateIdx ?? null,
    clientNavTransaction: params.clientNavTransaction ?? null,
    acceptedTransaction: params.acceptedTransaction ?? null,
    previousPath: params.previousPath ?? null,
    previousHref: params.previousHref ?? null,
    lastIntent: params.lastIntent ?? null,
    lastActionableIntent: params.lastActionableIntent ?? null,
    historyTreeSummary: params.historyTreeSummary ?? null,
    latestNavReplace: params.latestNavReplace ?? null,
    bottomNavStalePushState: params.bottomNavStalePushState ?? null,
  } satisfies PersistedHistoryEvent);
}

export function persistReportDebugLifecycleSnapshot(params: {
  event: PersistedLifecycleEvent["event"];
  value: string;
  ts?: number;
}) {
  if (typeof window === "undefined") return;

  const ts = typeof params.ts === "number" ? params.ts : Date.now();
  writePersistedSnapshot(PERSISTED_LIFECYCLE_EVENT_KEY, {
    event: params.event,
    value: params.value,
    ts,
    atIso: new Date(ts).toISOString(),
    href: getLocationHref(),
    pathname: getLocationPath(),
    visibilityState: typeof document !== "undefined" ? document.visibilityState : null,
  } satisfies PersistedLifecycleEvent);
}

export function persistReportDebugStartupTimelineEvent(params: {
  source: string;
  event: string;
  ts?: number;
  details?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  if (!readReportDebugFlag()) return;

  const ts = typeof params.ts === "number" ? params.ts : Date.now();
  const snapshot: PersistedStartupTimelineEvent = {
    source: params.source,
    event: params.event,
    ts,
    atIso: new Date(ts).toISOString(),
    path: getLocationPath(),
    href: getLocationHref(),
    visibilityState: typeof document !== "undefined" ? document.visibilityState : null,
    details: sanitizeTimelineDetails(params.details),
  };
  const existing = readPersistedArray<PersistedStartupTimelineEvent>(PERSISTED_STARTUP_TIMELINE_KEY);
  writePersistedSnapshot(
    PERSISTED_STARTUP_TIMELINE_KEY,
    [...existing, snapshot].slice(-PERSISTED_STARTUP_TIMELINE_MAX)
  );
}

export function persistReportDebugHistoryEditScrollSnapshot(
  snapshot: PersistedHistoryEditScrollDebugSnapshot | null
) {
  if (typeof window === "undefined") return;

  if (!snapshot) {
    try {
      window.sessionStorage.removeItem(PERSISTED_HISTORY_EDIT_SCROLL_DEBUG_KEY);
    } catch {
      // Ignore storage failures in debug-only instrumentation.
    }
    return;
  }

  writePersistedSnapshot(PERSISTED_HISTORY_EDIT_SCROLL_DEBUG_KEY, snapshot);
}

export function persistReportDebugHistoryEditNavigationSnapshot(
  snapshot: PersistedHistoryEditNavigationDebugSnapshot | null
) {
  if (typeof window === "undefined") return;

  if (!snapshot) {
    persistedHistoryEditNavigationDebugSnapshot = null;
    try {
      window.sessionStorage.removeItem(PERSISTED_HISTORY_EDIT_NAVIGATION_DEBUG_KEY);
    } catch {
      // Ignore storage failures in debug-only instrumentation.
    }
    return;
  }

  persistedHistoryEditNavigationDebugSnapshot = snapshot;
  writePersistedSnapshot(PERSISTED_HISTORY_EDIT_NAVIGATION_DEBUG_KEY, snapshot);
}

export function readReportDebugHistoryEditNavigationSnapshot(): PersistedHistoryEditNavigationDebugSnapshot | null {
  return (
    persistedHistoryEditNavigationDebugSnapshot ??
    readPersistedSnapshot<PersistedHistoryEditNavigationDebugSnapshot>(PERSISTED_HISTORY_EDIT_NAVIGATION_DEBUG_KEY)
  );
}

export function persistReportDebugProgramHomeBounceSnapshot(params: PersistedProgramHomeBounceEvent | null) {
  if (!params) {
    persistedProgramHomeBounceSnapshot = null;
    return;
  }

  persistedProgramHomeBounceSnapshot = params;
}

export function persistReportDebugHomeProgramCorrectionSnapshot(
  params: PersistedHomeProgramCorrectionDebugSnapshot | null
) {
  if (typeof window === "undefined") return;

  if (!params) {
    persistedHomeProgramCorrectionSnapshot = null;
    try {
      window.sessionStorage.removeItem(PERSISTED_HOME_PROGRAM_CORRECTION_KEY);
    } catch {
      // Ignore storage failures in debug-only instrumentation.
    }
    return;
  }

  persistedHomeProgramCorrectionSnapshot = params;
  writePersistedSnapshot(PERSISTED_HOME_PROGRAM_CORRECTION_KEY, params);
}

export function readReportDebugHomeProgramCorrectionSnapshot(): PersistedHomeProgramCorrectionDebugSnapshot | null {
  return (
    persistedHomeProgramCorrectionSnapshot ??
    readPersistedSnapshot<PersistedHomeProgramCorrectionDebugSnapshot>(PERSISTED_HOME_PROGRAM_CORRECTION_KEY)
  );
}

export function persistReportDebugGalleryHomeBounceSnapshot(params: PersistedGalleryHomeBounceEvent | null) {
  if (!params) {
    persistedGalleryHomeBounceSnapshot = null;
    return;
  }

  persistedGalleryHomeBounceSnapshot = params;
}

export function persistReportDebugProgramHomeRawHistorySnapshot(
  snapshot: PersistedProgramHomeRawHistoryDebugSnapshot | null
) {
  if (typeof window === "undefined") return;

  if (!snapshot) {
    persistedProgramHomeRawHistorySnapshot = null;
    try {
      window.sessionStorage.removeItem(PERSISTED_PROGRAM_HOME_RAW_HISTORY_KEY);
    } catch {
      // Ignore storage failures in debug-only instrumentation.
    }
    return;
  }

  persistedProgramHomeRawHistorySnapshot = snapshot;
  writePersistedSnapshot(PERSISTED_PROGRAM_HOME_RAW_HISTORY_KEY, snapshot);
}

export function persistReportDebugNutritionHomeBounceSnapshot(params: PersistedNutritionHomeBounceEvent | null) {
  if (!params) {
    persistedNutritionHomeBounceSnapshot = null;
    return;
  }

  persistedNutritionHomeBounceSnapshot = params;
}

export function persistReportDebugRenderedRouteSplitBrainSnapshot(
  snapshot: PersistedRenderedRouteSplitBrainSnapshot | null
) {
  if (typeof window === "undefined") return;

  if (!snapshot) {
    persistedRenderedRouteSplitBrainSnapshot = null;
    try {
      window.sessionStorage.removeItem(PERSISTED_RENDERED_ROUTE_SPLIT_BRAIN_KEY);
    } catch {
      // Ignore storage failures in debug-only instrumentation.
    }
    return;
  }

  persistedRenderedRouteSplitBrainSnapshot = snapshot;
  writePersistedSnapshot(PERSISTED_RENDERED_ROUTE_SPLIT_BRAIN_KEY, snapshot);
}

export function persistNutritionRouteGuardDecision(event: PersistedNutritionRouteGuardDecisionEvent) {
  if (typeof window === "undefined") return;

  const existing = readPersistedArray<PersistedNutritionRouteGuardDecisionEvent>(
    PERSISTED_NUTRITION_ROUTE_GUARD_DECISIONS_KEY
  );
  writePersistedSnapshot(
    PERSISTED_NUTRITION_ROUTE_GUARD_DECISIONS_KEY,
    [...existing, event].slice(-PERSISTED_NUTRITION_ROUTE_GUARD_DECISIONS_MAX)
  );
}

export function recordNutritionRouteGuardDecision(params: {
  guard: PersistedNutritionRouteGuardDecisionEvent["guard"];
  decision: PersistedNutritionRouteGuardDecisionEvent["decision"];
  reason: string;
  previousPath: string | null;
  currentPath: string | null;
  currentIntent:
    | {
        kind: string;
        isActionable: boolean;
        routeAtIntent: string;
        actionableHref?: string | null;
        actionableText?: string | null;
        inBottomTabs?: boolean;
        ts: number;
      }
    | null;
  lastActionableIntent:
    | {
        href: string;
        ts: number;
        routeAtIntent: string;
      }
    | null;
  nav:
    | {
        event: string;
        source: string;
        href?: string | null;
        ts: number;
      }
    | null;
  history:
    | {
        event: "pushState" | "replaceState" | "popstate";
        ts: number;
      }
    | null;
  latestTransaction:
    | {
        source: string;
        fromPath: string;
        toPath: string;
        reason: string;
        accepted: boolean;
        initiatedAtIso: string;
      }
    | null;
  popstateAgeMs: number;
  restoreHref: string | null;
  shadowDecision:
    | {
        kind?: string | null;
        reason?: string | null;
        handled?: boolean | null;
      }
    | null;
  previousHref?: string | null;
  ts?: number;
}) {
  const now = typeof params.ts === "number" ? params.ts : Date.now();
  const browserLocationHref = typeof window !== "undefined" ? window.location.href : null;
  const intentAgeMs = params.currentIntent ? Math.max(0, now - params.currentIntent.ts) : null;
  const lastActionableIntentAgeMs = params.lastActionableIntent ? Math.max(0, now - params.lastActionableIntent.ts) : null;
  const navAgeMs = params.nav ? Math.max(0, now - params.nav.ts) : null;
  const historyAgeMs = params.history ? Math.max(0, now - params.history.ts) : null;
  const latestTransactionAgeMs = params.latestTransaction
    ? (() => {
        const initiatedAtMs = Date.parse(params.latestTransaction.initiatedAtIso);
        return Number.isFinite(initiatedAtMs) ? Math.max(0, now - initiatedAtMs) : null;
      })()
    : null;

  persistNutritionRouteGuardDecision({
    ts: now,
    atIso: new Date(now).toISOString(),
    guard: params.guard,
    decision: params.decision,
    reason: params.reason,
    previousPath: params.previousPath,
    currentPath: params.currentPath,
    previousHref: params.previousHref ?? null,
    browserLocationHref,
    intentKind: params.currentIntent?.kind ?? null,
    intentIsActionable: params.currentIntent?.isActionable ?? null,
    intentAgeMs,
    intentRouteAtIntent: params.currentIntent?.routeAtIntent ?? null,
    intentActionableHref: params.currentIntent?.actionableHref ?? null,
    intentActionableText: params.currentIntent?.actionableText ?? null,
    intentInBottomTabs: params.currentIntent?.inBottomTabs ?? null,
    lastActionableIntentHref: params.lastActionableIntent?.href ?? null,
    lastActionableIntentAgeMs,
    lastActionableIntentRouteAtIntent: params.lastActionableIntent?.routeAtIntent ?? null,
    navEvent: params.nav?.event ?? null,
    navSource: params.nav?.source ?? null,
    navHrefPath: params.nav?.href ? safePathFromHref(params.nav.href) ?? params.nav.href : null,
    navAgeMs,
    popstateAgeMs: Number.isFinite(params.popstateAgeMs) ? Math.max(0, Math.round(params.popstateAgeMs)) : null,
    historyEvent: params.history?.event ?? null,
    historyAgeMs,
    hasTransaction: params.latestTransaction !== null,
    latestTransactionSource: params.latestTransaction?.source ?? null,
    latestTransactionFromPath: params.latestTransaction?.fromPath ?? null,
    latestTransactionToPath: params.latestTransaction?.toPath ?? null,
    latestTransactionReason: params.latestTransaction?.reason ?? null,
    latestTransactionAccepted: params.latestTransaction?.accepted ?? null,
    latestTransactionAgeMs,
    restoreHref: params.restoreHref,
    shadowDecisionKind: params.shadowDecision?.kind ?? null,
    shadowDecisionReason: params.shadowDecision?.reason ?? null,
    shadowDecisionHandled: params.shadowDecision?.handled ?? null,
  });
}

export function readNutritionRouteGuardDecisions(): PersistedNutritionRouteGuardDecisionEvent[] {
  return readPersistedArray<PersistedNutritionRouteGuardDecisionEvent>(PERSISTED_NUTRITION_ROUTE_GUARD_DECISIONS_KEY);
}

export function persistHomeWeightGuardDecision(event: PersistedHomeWeightGuardDecisionEvent) {
  if (typeof window === "undefined") return;

  const existing = readPersistedArray<PersistedHomeWeightGuardDecisionEvent>(PERSISTED_HOME_WEIGHT_GUARD_DECISIONS_KEY);
  writePersistedSnapshot(
    PERSISTED_HOME_WEIGHT_GUARD_DECISIONS_KEY,
    [...existing, event].slice(-PERSISTED_HOME_WEIGHT_GUARD_DECISIONS_MAX)
  );
}

export function readHomeWeightGuardDecisions(): PersistedHomeWeightGuardDecisionEvent[] {
  return readPersistedArray<PersistedHomeWeightGuardDecisionEvent>(PERSISTED_HOME_WEIGHT_GUARD_DECISIONS_KEY);
}

export function persistWeightMeasurementsGuardDecision(event: PersistedWeightMeasurementsGuardDecisionEvent) {
  if (typeof window === "undefined") return;

  const existing = readPersistedArray<PersistedWeightMeasurementsGuardDecisionEvent>(PERSISTED_WEIGHT_MEASUREMENTS_GUARD_DECISIONS_KEY);
  writePersistedSnapshot(
    PERSISTED_WEIGHT_MEASUREMENTS_GUARD_DECISIONS_KEY,
    [...existing, event].slice(-PERSISTED_WEIGHT_MEASUREMENTS_GUARD_DECISIONS_MAX)
  );
}

export function readWeightMeasurementsGuardDecisions(): PersistedWeightMeasurementsGuardDecisionEvent[] {
  return readPersistedArray<PersistedWeightMeasurementsGuardDecisionEvent>(PERSISTED_WEIGHT_MEASUREMENTS_GUARD_DECISIONS_KEY);
}

export function getReportDebugRouteContext(nowMs = Date.now()) {
  if (typeof window === "undefined") {
    return {
      currentPath: null,
      currentHref: null,
      historyLength: null,
      visibilityState: null,
      wasDiscarded: null,
      navigationType: null,
      navigationActivationStartMs: null,
      referrerPath: null,
      storedCurrentRoute: null,
      storedCurrentRouteHref: null,
      storedCurrentRouteAtIso: null,
      storedPreviousRoute: null,
      storedPreviousRouteHref: null,
      storedPreviousRouteAtIso: null,
      storedRouteAgeMs: null,
      storedPreviousRouteAgeMs: null,
      jsContextGeneration: null,
      lastHistoryEvent: null,
      lastLifecycleEvent: null,
      startupTimeline: [],
    };
  }

  const navigationEntry = getNavigationEntry();
  const storedRouteTimestamp = Number.parseInt(window.sessionStorage.getItem("clientRouteTimestamp") || "", 10);
  const storedPreviousRouteTimestamp = Number.parseInt(
    window.sessionStorage.getItem("clientPreviousRouteTimestamp") || "",
    10
  );
  const persistedHistoryEvent = readPersistedSnapshot<PersistedHistoryEvent>(PERSISTED_HISTORY_EVENT_KEY);
  const persistedLifecycleEvent = readPersistedSnapshot<PersistedLifecycleEvent>(PERSISTED_LIFECYCLE_EVENT_KEY);
  const startupTimeline = readPersistedArray<PersistedStartupTimelineEvent>(PERSISTED_STARTUP_TIMELINE_KEY);
  const documentWithDiscardState = document as Document & { wasDiscarded?: boolean };
  const jsContextGeneration = getJsContextGeneration();

  return {
    currentPath: getLocationPath(),
    currentHref: getLocationHref(),
    historyLength: window.history.length,
    visibilityState: document.visibilityState,
    wasDiscarded:
      typeof documentWithDiscardState.wasDiscarded === "boolean" ? documentWithDiscardState.wasDiscarded : null,
    navigationType: navigationEntry?.type ?? null,
    navigationActivationStartMs: getNavigationActivationStartMs(navigationEntry),
    referrerPath: sameOriginReferrerPath(document.referrer),
    storedCurrentRoute: window.sessionStorage.getItem("clientCurrentRoute"),
    storedCurrentRouteHref: window.sessionStorage.getItem("clientCurrentRouteHref"),
    storedCurrentRouteAtIso: Number.isFinite(storedRouteTimestamp) ? new Date(storedRouteTimestamp).toISOString() : null,
    storedPreviousRoute: window.sessionStorage.getItem("clientPreviousRoute"),
    storedPreviousRouteHref: window.sessionStorage.getItem("clientPreviousRouteHref"),
    storedPreviousRouteAtIso: Number.isFinite(storedPreviousRouteTimestamp)
      ? new Date(storedPreviousRouteTimestamp).toISOString()
      : null,
    storedRouteAgeMs: Number.isFinite(storedRouteTimestamp) ? Math.max(0, nowMs - storedRouteTimestamp) : null,
    storedPreviousRouteAgeMs: Number.isFinite(storedPreviousRouteTimestamp)
      ? Math.max(0, nowMs - storedPreviousRouteTimestamp)
      : null,
    jsContextGeneration,
    lastHistoryEvent: persistedHistoryEvent
      ? {
          event: persistedHistoryEvent.event,
          path: persistedHistoryEvent.path,
          url: persistedHistoryEvent.url,
          pathname: persistedHistoryEvent.pathname,
          href: persistedHistoryEvent.href,
          atIso: persistedHistoryEvent.atIso,
          ageMs: Math.max(0, nowMs - persistedHistoryEvent.ts),
          stackSummary: persistedHistoryEvent.stackSummary,
          fullStack: persistedHistoryEvent.fullStack ?? null,
          jsContextGeneration: persistedHistoryEvent.jsContextGeneration ?? null,
          clientNavTransactionId: persistedHistoryEvent.clientNavTransactionId ?? null,
          acceptedTransactionId: persistedHistoryEvent.acceptedTransactionId ?? null,
          callerFnName: persistedHistoryEvent.callerFnName ?? null,
          callerFileName: persistedHistoryEvent.callerFileName ?? null,
          callerLineNumber: persistedHistoryEvent.callerLineNumber ?? null,
          appLevelCallerFnName: persistedHistoryEvent.appLevelCallerFnName ?? null,
          appLevelCallerFileName: persistedHistoryEvent.appLevelCallerFileName ?? null,
          appLevelCallerLineNumber: persistedHistoryEvent.appLevelCallerLineNumber ?? null,
          acceptedTransactionFromPath: persistedHistoryEvent.acceptedTransactionFromPath ?? null,
          acceptedTransactionToPath: persistedHistoryEvent.acceptedTransactionToPath ?? null,
          historyStateKeys: persistedHistoryEvent.historyStateKeys ?? null,
          historyStateIdx: persistedHistoryEvent.historyStateIdx ?? null,
          clientNavTransaction: persistedHistoryEvent.clientNavTransaction ?? null,
          acceptedTransaction: persistedHistoryEvent.acceptedTransaction ?? null,
          previousPath: persistedHistoryEvent.previousPath ?? null,
          previousHref: persistedHistoryEvent.previousHref ?? null,
          lastIntent: persistedHistoryEvent.lastIntent ?? null,
          lastActionableIntent: persistedHistoryEvent.lastActionableIntent ?? null,
        }
      : null,
    lastLifecycleEvent: persistedLifecycleEvent
      ? {
          event: persistedLifecycleEvent.event,
          value: persistedLifecycleEvent.value,
          pathname: persistedLifecycleEvent.pathname,
          href: persistedLifecycleEvent.href,
          visibilityState: persistedLifecycleEvent.visibilityState,
          atIso: persistedLifecycleEvent.atIso,
          ageMs: Math.max(0, nowMs - persistedLifecycleEvent.ts),
        }
      : null,
    startupTimeline: startupTimeline.map((entry) => ({
      source: entry.source,
      event: entry.event,
      path: entry.path,
      href: entry.href,
      visibilityState: entry.visibilityState,
      atIso: entry.atIso,
      ageMs: Math.max(0, nowMs - entry.ts),
      details: entry.details ?? null,
    })),
  };
}

export function readReportDebugProgramHomeBounceSnapshot(): PersistedProgramHomeBounceEvent | null {
  return persistedProgramHomeBounceSnapshot ?? readPersistedSnapshot<PersistedProgramHomeBounceEvent>(PERSISTED_PROGRAM_HOME_BOUNCE_KEY);
}

export function readReportDebugGalleryHomeBounceSnapshot(): PersistedGalleryHomeBounceEvent | null {
  return persistedGalleryHomeBounceSnapshot ?? readPersistedSnapshot<PersistedGalleryHomeBounceEvent>(PERSISTED_GALLERY_HOME_BOUNCE_KEY);
}

export function readReportDebugProgramHomeRawHistorySnapshot(): PersistedProgramHomeRawHistoryDebugSnapshot | null {
  return (
    persistedProgramHomeRawHistorySnapshot ??
    readPersistedSnapshot<PersistedProgramHomeRawHistoryDebugSnapshot>(PERSISTED_PROGRAM_HOME_RAW_HISTORY_KEY)
  );
}

export function readReportDebugNutritionHomeBounceSnapshot(): PersistedNutritionHomeBounceEvent | null {
  return persistedNutritionHomeBounceSnapshot ?? readPersistedSnapshot<PersistedNutritionHomeBounceEvent>(PERSISTED_NUTRITION_HOME_BOUNCE_KEY);
}

export function readReportDebugRenderedRouteSplitBrainSnapshot(): PersistedRenderedRouteSplitBrainSnapshot | null {
  return (
    persistedRenderedRouteSplitBrainSnapshot ??
    readPersistedSnapshot<PersistedRenderedRouteSplitBrainSnapshot>(PERSISTED_RENDERED_ROUTE_SPLIT_BRAIN_KEY)
  );
}

export function readReportDebugHistoryEditScrollSnapshot(): PersistedHistoryEditScrollDebugSnapshot | null {
  const persisted = readPersistedSnapshot<PersistedHistoryEditScrollDebugSnapshot>(PERSISTED_HISTORY_EDIT_SCROLL_DEBUG_KEY);
  if (persisted) return persisted;

  const liveSnapshot = buildHistoryEditScrollDebugSnapshotFromDom("report-read-fallback");
  if (!liveSnapshot) return null;

  writePersistedSnapshot(PERSISTED_HISTORY_EDIT_SCROLL_DEBUG_KEY, liveSnapshot);
  return liveSnapshot;
}
